import { useMemo } from "react";
import * as React from "react";

import SelectClinic from "components/custom-filters-list/filters/select-clinic.component";
import SelectPatient from "components/custom-filters-list/filters/select-patient.component";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import SelectTag from "components/custom-filters-list/filters/select-tag.component";
import Select from "components/custom-filters-list/filters/select.component";
import useCustomFilters, {
  FilterOption
} from "components/custom-filters-list/filters/use-custom-filters.hook";
import { TagTypes } from "constants/tags";
import { FaxesQueryParams } from "services/types/fax.type";
import { FAXES_DIRECTIONS } from "services/types/fax.type";

export type FaxesCustomFiltersValues = {
  patient?: { label: string; value: string } | null;
  sender?: { label: string; value: string }[] | null;
  direction?: { label: string; value: string } | null;
  assigned_tags?: { label: string; value: boolean }[] | null;
  clinic?: { label: string; value: string } | null;
};

export const PAYMENTS_DEFAULT_FILTERS: FaxesCustomFiltersValues = {
  sender: null,
  patient: null,
  direction: null,
  assigned_tags: null,
  clinic: null
};

type UseFaxesFilterOptionsProps = {
  updateCustomFilter: (
    filterName: keyof FaxesQueryParams,
    filterValue: FaxesQueryParams[keyof FaxesQueryParams] | undefined | null
  ) => void;
  selectedCustomFilters: FaxesCustomFiltersValues;
  setSelectedCustomFilters: React.Dispatch<
    React.SetStateAction<FaxesCustomFiltersValues>
  >;
};

export default function useFaxesCustomFilterOptions({
  updateCustomFilter,
  selectedCustomFilters,
  setSelectedCustomFilters
}: UseFaxesFilterOptionsProps) {
  const filterOptions: FilterOption<
    FaxesCustomFiltersValues,
    FaxesQueryParams
  >[] = useMemo(
    () => [
      {
        name: "patient",
        label: "Patient",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Patient: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectPatient
            name="patient"
            control={control}
            onOptionSelected={(option) => {
              updateFilter(option?.value ? Number(option.value) : undefined);
              setMetadata(option);
            }}
            openOnFocus
            showInactive
          />
        )
      },
      {
        name: "direction",
        label: "Direction",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Direction: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <Select
            name="direction"
            label="Direction"
            options={FAXES_DIRECTIONS.getOptions()}
            control={control}
            onOptionSelected={(option) => {
              updateFilter(option?.value ? String(option.value) : undefined);
              setMetadata(option);
            }}
          />
        )
      },
      {
        name: "assigned_tags",
        label: "Tag",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Tag:{" "}
            <b>
              {metadata?.length > 1 ? (
                <>{`${metadata?.length} Selected`}</>
              ) : (
                metadata?.map((item: { label: string }) => item?.label)
              )}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectTag
            name="assigned_tags"
            tagType={TagTypes.FAX}
            control={control}
            onOptionSelected={(option) => {
              updateFilter(
                Array.isArray(option)
                  ? option
                      ?.map((item: { value: string }) => item?.value)
                      .join(",")
                  : option?.value
              );

              setMetadata(option);
            }}
          />
        )
      },
      {
        name: "clinic",
        label: "Clinic",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Clinic: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectClinic
            name="clinic"
            showAllRegions
            skipFilteringByUser
            control={control}
            onOptionSelected={(option) => {
              updateFilter(
                option && !Array.isArray(option) ? option.value : null
              );
              setMetadata(option);
            }}
            filterByRegions={[]}
          />
        )
      },
      {
        name: "sender",
        label: "Sender",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Sender:{" "}
            <b>
              {metadata?.length > 1 ? (
                <>{`${metadata?.length} Selected`}</>
              ) : (
                metadata?.map((item: { label: string }) => item?.label)
              )}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectProvider
            name="sender"
            control={control}
            label="By Sender"
            ignoreRegionsFilter
            ignoreClinicsFilter
            autoFocus
            onOptionSelected={(option) => {
              updateFilter(
                Array.isArray(option)
                  ? option
                      ?.map((item: { value: string }) => item?.value)
                      .join(",")
                  : option?.value
              );
              setMetadata(option);
            }}
            multiple
            openOnFocus
          />
        )
      }
    ],
    []
  );

  const filters = useCustomFilters<FaxesCustomFiltersValues, FaxesQueryParams>({
    updateCustomFilter,
    filterOptions,
    selectedCustomFilters,
    setSelectedCustomFilters
  });

  return filters;
}
